/* Forgot password.
 *
 * The web half of the mobile flow, which existed first: the endpoints have
 * been live all along and this screen simply was not built, so somebody who
 * forgot their password on the web had no route back into their account.
 *
 * THREE STEPS, AND EVERY ONE OF THEM ESCAPABLE. The mobile version taught this
 * the hard way — it dead-ended if you mistyped the number, because the only
 * screen you could reach asked for a code that would never arrive. Both "Use a
 * different number" links below exist for that reason.
 *
 * The code is verified by the reset call itself, not by a separate request:
 * POST /auth/reset-password takes request_id + code + the new password
 * together. So step 2 only checks the code looks plausible, and a wrong one
 * surfaces on step 3 — at which point we send them back to step 2 rather than
 * leaving them on a form that cannot succeed.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { PhoneField } from '@/components/ui/phone-field'
import { DEFAULT_COUNTRY, prettyPhone, toE164, type Country } from '@/lib/countries'
import { api, ApiError } from '@/api/client'

type Challenge = {
  request_id: string
  ttl_s: number
  /** Dev only — the API echoes the code until DLT sender-ID approval clears. */
  dev_code?: string | null
}

type Step = 'mobile' | 'code' | 'password' | 'done'

export function ForgotPasswordPage() {
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('mobile')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [mobile, setMobile] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')

  const [requestId, setRequestId] = useState<string | null>(null)
  const [devCode, setDevCode] = useState<string | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /** Back to step 1, forgetting the challenge — the number was wrong, so the
   *  request_id tied to it is worthless. */
  function startOver() {
    setStep('mobile')
    setCode('')
    setDevCode(null)
    setRequestId(null)
    setError(null)
  }

  async function sendCode(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.post<Challenge>(
        '/auth/forgot-password',
        { destination: toE164(country, mobile) },
        { auth: false },
      )
      setRequestId(res.request_id)
      if (res.dev_code) {
        setDevCode(res.dev_code)
        setCode(res.dev_code)
      }
      setStep('code')
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  async function reset(e: FormEvent) {
    e.preventDefault()
    if (busy || !requestId) return
    setBusy(true)
    setError(null)
    try {
      await api.post(
        '/auth/reset-password',
        {
          request_id: requestId,
          destination: toE164(country, mobile),
          code: code.trim(),
          new_password: password,
        },
        { auth: false },
      )
      setStep('done')
    } catch (err) {
      setError(messageFor(err))
      // A bad code belongs on the code step, not on a password form that can
      // never submit. Same rule as mobile.
      if (err instanceof ApiError && err.code?.startsWith('otp_')) {
        setStep('code')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] place-items-center bg-canvas p-s5">
      <div className="w-full max-w-[420px] rounded-md bg-surface p-s6 shadow-2">
        <div className="mb-s6 flex flex-col items-center gap-s3">
          <div className="grid h-[60px] w-[60px] place-items-center rounded-pill bg-brand">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none"
                 stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 4v16M6.5 9.5h11" />
            </svg>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold tracking-tight text-brand">
              Connect In God
            </div>
            <p className="mt-1 text-base text-ink-2">
              {step === 'done' ? 'Password changed' : 'Reset your password'}
            </p>
          </div>
        </div>

        {error ? (
          <div
            role="alert"
            className="mb-s4 flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}

        {step === 'mobile' ? (
          <form onSubmit={sendCode} className="flex flex-col gap-s4">
            <PhoneField
              country={country}
              onCountryChange={setCountry}
              value={mobile}
              onValueChange={setMobile}
              help="We send a reset code to this number"
              required
              autoFocus
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              full
              loading={busy}
              disabled={!mobile}
            >
              Send code
            </Button>
          </form>
        ) : null}

        {step === 'code' ? (
          <div className="flex flex-col gap-s4">
            <p className="text-base text-ink-2">
              We sent a 6-digit code to{' '}
              <span className="font-semibold text-ink">
                {prettyPhone(country, mobile)}
              </span>
              .
            </p>
            <Field
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              /* Show the code itself, not just "it is filled in". On a dev
                 build there is no SMS, so if the field is cleared the code is
                 unrecoverable and the flow is a dead end. */
              help={devCode ? `Test mode — your code is ${devCode}` : undefined}
            />
            <Button
              variant="primary"
              size="lg"
              full
              disabled={code.trim().length < 4}
              onClick={() => {
                setStep('password')
                setError(null)
              }}
            >
              Continue
            </Button>
            <button
              type="button"
              onClick={startOver}
              className="flex items-center justify-center gap-s2 text-base text-ink-2 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Use a different number
            </button>
          </div>
        ) : null}

        {step === 'password' ? (
          <form onSubmit={reset} className="flex flex-col gap-s4">
            <Field
              label="New password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              reveal
              help="At least 8 characters"
              minLength={8}
              required
              autoFocus
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              full
              loading={busy}
              disabled={password.length < 8}
            >
              Change password
            </Button>
            <button
              type="button"
              onClick={() => {
                setStep('code')
                setError(null)
              }}
              className="flex items-center justify-center gap-s2 text-base text-ink-2 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
          </form>
        ) : null}

        {step === 'done' ? (
          <div className="flex flex-col items-center gap-s4">
            <div className="grid h-[72px] w-[72px] place-items-center rounded-pill bg-success-soft">
              <Check className="h-8 w-8 text-success" aria-hidden />
            </div>
            <p className="text-center text-base text-ink-2">
              Your password has been changed. Sign in with your new one.
            </p>
            <Button
              variant="primary"
              size="lg"
              full
              onClick={() => navigate('/signin')}
            >
              Sign in
            </Button>
          </div>
        ) : null}

        {step !== 'done' ? (
          <p className="mt-s5 text-center text-base text-ink-2">
            Remembered it?{' '}
            <Link to="/signin" className="font-semibold text-brand hover:underline">
              Sign in
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  )
}

function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Something went wrong. Please try again.'
  if (err.status === 0) {
    return 'You appear to be offline. Check your connection and try again.'
  }
  if (err.status === 429) {
    return 'Too many attempts. Wait a minute before trying again.'
  }
  return err.message
}
